/** The normalized shape every product card/grid consumes (real API or mock). */
export type DisplayProduct = {
  id: string;
  name: string;
  price: number; // effective unit price (cash)
  was: number; // strikethrough original
  off: number; // discount %
  pv: number; // PV value per unit
  // Flat per-currency sticker prices (no discount). null = not sold in that
  // currency. Used when the buyer picks a GC/coin payment method at checkout.
  priceGc: number | null;
  priceCoin: number | null;
  // Real available stock (member-priced storefront only). undefined on the
  // public/unauthenticated path, which carries no per-buyer inventory context;
  // null when the product isn't stock-tracked at all (virtual). Both render as
  // "no stock line" — every read here is a `!= null` check.
  stockQty?: number | null;
  // True when a kit's stock is only reachable by assembling its components.
  assembledFromComponents?: boolean;
  categoryId: string | null; // for the dynamic category filter
  rating: number; // filled stars 1–5
  image: string | null; // resolved image URL, or null for striped placeholder
  // Free-shipping tag (member-priced storefront only; undefined on the public path).
  freeShipping?: boolean;
  // Virtual product / service — nothing to deliver. Present on BOTH the public
  // and member-priced paths.
  isVirtual?: boolean;
  // Short marketing copy, shown in the virtual-product hover card. Present on
  // both paths (null when the product has none).
  description?: string | null;
  // Slug of the enabled campaign page this product sells through (null = none).
  // Drives `virtual_products_open_replicated_page` — see `productHref`.
  replicatedPageSlug?: string | null;
  // Buyer cashback per unit = Realtime Unilevel Level 0 (member path only; 0 = none).
  cashback?: number;
  // Dynamic voucher badge label from a tagged coupon, e.g. "₱50 OFF" (undefined = none).
  voucherBadge?: string;
  // Admin "Featured" flag. Boosts sort order in Shop/search, and EXCLUDES the
  // product from the storefront home's product feed (it's already highlighted
  // elsewhere on that page). Present on BOTH the public and member-priced paths.
  featured?: boolean;
  sellerId: string; // '' = Omni Official (house), else merchantUserId
  sellerName: string;
  // Product variations (empty for single-SKU products). The detail page renders
  // a picker; selecting one carries its id + price into the cart line.
  variants: ProductVariant[];
};

/** A purchasable variation as the storefront UI consumes it. */
export type ProductVariant = {
  id: string;
  name: string;
  attributes: Record<string, string>;
  price: number;
  /** Compare-at / slashed price (0 when there's no slash). */
  was: number;
  /** Flat per-currency sticker prices (no discount). null = not sold in that currency. */
  priceGc: number | null;
  priceCoin: number | null;
  inStock: boolean;
  /** Available units for this variant — caps the quantity stepper. */
  stockQty: number;
};

/** Striped-placeholder tone class, cycling t1–t6 by index. */
export const tone = (i: number) => 't' + ((i % 6) + 1);
